import { Box, IconButton, Skeleton, Typography } from "@mui/material"
import { EditOutlined } from "@mui-symbols-material/w300"
import type { Row } from "@tanstack/react-table"
import dayjs from "dayjs"
import type { ReactElement } from "react"
import { useShallow } from "zustand/shallow"

import { usePriceUomColumn } from "../hooks/usePriceUomColumn"

import { WarningMessage } from "@/components/WarningMessage/WarningMessage"
import { DATE_FORMAT, UOM_RENDER_PRECISION, UOM_ROUNDING_PRECISION } from "@/constants/formats"
import type { OrderLine, UnitOfMeasurement } from "@/graphql/codegen/graphql"
import { usePurchaseOrderReadonly } from "@/screens/Procurement/PurchaseOrder/hooks/usePurchaseOrderReadonly"
import { useUnitOfMeasurementsStore } from "@/stores/unitOfMeasurmentsStore"
import { formatNumber } from "@/utils/number"

export const PriceUomColumn = ({
  row,
  onOpenPriceDialog,
}: {
  row: Row<OrderLine>
  onOpenPriceDialog?: (rowContent: Row<OrderLine>, pricingUom: UnitOfMeasurement) => void
}): ReactElement => {
  const { convertedPrice, price, total, fetching, isManualPriceSet, pricingPackage } = usePriceUomColumn({
    row,
  })
  const { isReadonly } = usePurchaseOrderReadonly()

  const units = useUnitOfMeasurementsStore(useShallow((s) => s.units))

  const priceUom = units.find((unit) => unit.id === price?.uomId)

  const convertedPriceUom = units.find((unit) => unit.id === convertedPrice?.uomId)

  const hasPriceTier = !isManualPriceSet && pricingPackage && price

  return (
    <Box className='h-24 content-center gap-2'>
      {fetching ? (
        <Skeleton variant='text' width={150} />
      ) : (
        <>
          {isManualPriceSet && <WarningMessage message='Overridden' />}
          {hasPriceTier && (
            <Box className='mb-1'>
              <Typography variant='caption' className='ml-1 text-green-900' data-testid='po-price-effective-date'>
                Effective {dayjs.utc(pricingPackage.effectiveDate).format(DATE_FORMAT.US_SHORT)}
              </Typography>
            </Box>
          )}
        </>
      )}
      <div className='flex gap-2'>
        <Typography variant='h6' className='font-bold text-gray-700' data-testid='po-price-per-uom'>
          {fetching ? (
            <Skeleton variant='text' width={150} />
          ) : price ? (
            `$${formatNumber(price.amount, UOM_ROUNDING_PRECISION)} / ${priceUom?.name ?? ""}`
          ) : (
            "Not calculated"
          )}
        </Typography>
        {!fetching && onOpenPriceDialog && (
          <IconButton
            color='primary'
            className='p-0'
            size='small'
            disabled={isReadonly}
            aria-label='edit price per unit of measurement'
            onClick={() => onOpenPriceDialog(row, priceUom!)}
            data-testid='po-edit-price-per-uom-button'
          >
            <EditOutlined />
          </IconButton>
        )}
      </div>
      <Typography variant='caption' className='pt-2 text-gray-700' data-testid='po-price-per-uom-converted'>
        {fetching ? (
          <Skeleton variant='text' width={100} />
        ) : (
          convertedPrice &&
          `$${formatNumber(convertedPrice.amount, UOM_ROUNDING_PRECISION)} / ${convertedPriceUom?.name ?? ""}`
        )}
      </Typography>

      <Typography variant='subtitle2' className='mt-3 text-gray-700' data-testid='po-price-total'>
        {fetching ? <Skeleton variant='text' width={100} /> : <>Total: ${formatNumber(total, UOM_RENDER_PRECISION)}</>}
      </Typography>
    </Box>
  )
}
